import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash, PencilSimple, X, User, Hash, Bank } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function People() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [people, setPeople] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", notes: "" });
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/people");
    setPeople(data);
    const bres = await api.get("/banks");
    setBanks(bres.data);
  };
  useEffect(() => { load(); }, []);

  const [banks, setBanks] = useState([]);
  const [newBank, setNewBank] = useState("");
  const addBank = async (e) => {
    e.preventDefault();
    const name = newBank.trim();
    if (!name) return;
    try {
      await api.post("/banks", { name });
      toast.success("Bank added");
      setNewBank("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };
  const delBank = async (b) => {
    if (!window.confirm(`Delete bank "${b.name}"?`)) return;
    try {
      await api.delete(`/banks/${b.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", code: "", notes: "" });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, code: p.code, notes: p.notes || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/people/${editing.id}`, form);
        toast.success("Person updated");
      } else {
        await api.post("/people", form);
        toast.success("Person added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete person "${p.name}" (${p.code})?`)) return;
    try {
      await api.delete(`/people/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const shown = people.filter((p) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return p.name.toLowerCase().includes(s) || p.code.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#2cb4a6]">Directory</div>
          <h1 className="font-display font-black text-4xl md:text-5xl leading-none">People.</h1>
          <p className="text-sm text-[#4a5578] mt-2 max-w-xl">
            Add every person the office deals with (staff, vendors, drivers…) each with a short code. They'll appear as suggestions in the transaction entry.
          </p>
        </div>
        {isAdmin && (
          <button onClick={openCreate} className="brut-btn brut-btn-primary flex items-center gap-2" data-testid="add-person-btn">
            <Plus size={16} weight="bold" /> Add person
          </button>
        )}
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name or code…"
        className="brut-input max-w-md"
        data-testid="people-search"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="people-grid">
        {shown.map((p) => (
          <div key={p.id} className="brut-card p-5 flex justify-between gap-4" data-testid={`person-card-${p.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <User size={18} weight="bold" color="#1e2a6b" />
                <span className="chip chip-upi font-mono-fin">
                  <Hash size={10} weight="bold" />{p.code}
                </span>
              </div>
              <div className="font-display font-black text-xl leading-tight break-words">{p.name}</div>
              {p.notes && <div className="text-xs text-[#4a5578] mt-1">{p.notes}</div>}
            </div>
            {isAdmin && (
              <div className="flex flex-col gap-1">
                <button onClick={() => openEdit(p)} className="brut-btn brut-btn-secondary p-2" data-testid={`edit-person-${p.id}`}>
                  <PencilSimple size={14} weight="bold" />
                </button>
                <button onClick={() => del(p)} className="brut-btn brut-btn-danger p-2" data-testid={`del-person-${p.id}`}>
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            )}
          </div>
        ))}
        {shown.length === 0 && (
          <div className="col-span-full brut-card p-10 text-center text-[#4a5578]">No people found.</div>
        )}
      </div>

      {/* Banks section */}
      <div className="brut-card p-5" data-testid="banks-section">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="brut-label">Registered banks</div>
            <div className="font-display font-black text-2xl flex items-center gap-2">
              <Bank size={22} weight="bold" color="#2cb4a6" /> Bank list
            </div>
          </div>
          <div className="text-xs text-[#4a5578]">These appear in the Bank dropdown when adding a transaction.</div>
        </div>
        {isAdmin && (
          <form onSubmit={addBank} className="flex gap-2 mb-4" data-testid="add-bank-form">
            <input
              value={newBank}
              onChange={(e) => setNewBank(e.target.value)}
              placeholder="e.g. HDFC / ICICI / SBI"
              className="brut-input flex-1"
              data-testid="new-bank-name"
            />
            <button type="submit" className="brut-btn brut-btn-primary flex items-center gap-2" data-testid="add-bank-btn">
              <Plus size={16} weight="bold" /> Add bank
            </button>
          </form>
        )}
        <div className="flex flex-wrap gap-2" data-testid="banks-list">
          {banks.map((b) => (
            <div key={b.id} className="chip chip-upi flex items-center gap-2" data-testid={`bank-chip-${b.id}`}>
              <Bank size={12} weight="bold" />
              <span>{b.name}</span>
              {isAdmin && (
                <button onClick={() => delBank(b)} className="ml-1 hover:text-[#c53030]" title="Delete" data-testid={`del-bank-${b.id}`}>
                  <X size={12} weight="bold" />
                </button>
              )}
            </div>
          ))}
          {banks.length === 0 && (
            <div className="text-sm text-[#4a5578]">No banks registered yet.</div>
          )}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-[#0f1b4c]/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="brut-card-lg w-full max-w-md p-6" data-testid="person-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="brut-label text-[#2cb4a6]">{editing ? "Edit person" : "New person"}</div>
                <h3 className="font-display font-black text-2xl">{editing ? "Update person" : "Add person"}</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="brut-btn brut-btn-secondary p-2">
                <X size={16} weight="bold" />
              </button>
            </div>
            <label className="brut-label block mb-2">Full name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Santhosh" className="brut-input mb-4" data-testid="person-name" />

            <label className="brut-label block mb-2">Short code (auto uppercased)</label>
            <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="e.g. SAN" maxLength={12} className="brut-input mb-4 uppercase" data-testid="person-code" />

            <label className="brut-label block mb-2">Notes (optional)</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="brut-input mb-6" data-testid="person-notes" />

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="brut-btn brut-btn-secondary">Cancel</button>
              <button type="submit" className="brut-btn brut-btn-primary" data-testid="person-save">
                {editing ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
